import { assert } from '@ember/debug';
import Service, { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import { fetch } from 'later/utils/fetch';
import loadScript from 'later/utils/load-script';
import getCountryList from 'shared/utils/country-list';

import type {
  Stripe as StripeClient,
  StripePaymentElement,
  StripeElementsOptionsMode,
  StripePaymentElementOptions,
  StripeElements,
  SetupIntent,
  ConfirmationToken,
  PaymentMethod,
  PaymentWalletOption,
  Layout
} from '@stripe/stripe-js';
import type IntlService from 'ember-intl/services/intl';
import type UserAgentService from 'ember-useragent/services/user-agent';
import type AuthService from 'later/services/auth';
import type DatadogService from 'later/services/datadog';
import type ErrorsService from 'later/services/errors';
import type LaterConfigService from 'later/services/later-config';
import type SegmentService from 'later/services/segment';
import type { PlanType } from 'shared/types/plans';
import type { JsonObject } from 'type-fest';

/**
 *
 * This service exists as an Interface for the Stripe JS SDK.
 * Details of the SDK can be found here https://stripe.com/docs/stripe-js/reference.
 *
 * @class StripeService
 * @extends Service
 */

export default class StripeService extends Service {
  @service declare auth: AuthService;
  @service declare laterConfig: LaterConfigService;
  @service declare errors: ErrorsService;
  @service declare intl: IntlService;
  @service declare segment: SegmentService;
  @service declare datadog: DatadogService;
  @service declare userAgent: UserAgentService;

  @tracked selectedPaymentType?: 'apple_pay' | 'google_pay' | 'card' | (string & NonNullable<unknown>);

  client?: StripeClient = undefined;
  incompleteWalletError = 'incomplete wallet';

  get stripePublishableKey(): string {
    return this.laterConfig.stripePublishableKey;
  }

  get isWalletSelected(): boolean {
    return this.selectedPaymentType === 'google_pay' || this.selectedPaymentType == 'apple_pay';
  }

  get isCardSelected(): boolean {
    return this.selectedPaymentType === 'card';
  }

  get canApplePay(): boolean {
    return Boolean(this.auth.currentAccount.canTrialPlan);
  }

  get applePayConfig(): PaymentWalletOption {
    return this.canApplePay ? 'auto' : 'never';
  }

  get canGooglePay(): boolean {
    return Boolean(this.auth.currentAccount.canTrialPlan);
  }

  get googlePayConfig(): PaymentWalletOption {
    return this.canGooglePay ? 'auto' : 'never';
  }

  get paymentElementLayoutConfig(): Layout {
    return this.userAgent.device.isMobile ? 'tabs' : 'accordion';
  }

  /**
   * Creates a "Stripe Payment Element"  input box
   * which is injected into the supplied HTML element
   *
   * @method createStripePaymentElement
   * @param elementString A string to identify the HTML element
   * which will be used to create a Stripe Card Input. Ex: `#stripe-payment-element-div`
   *
   * @returns The Stripe Elements object and the Stripe Payment Element
   */
  async createStripePaymentElement(args: {
    elementString: string;
    amount: number;
    planType?: PlanType;
  }): Promise<{ elements?: StripeElements; paymentElement?: StripePaymentElement | undefined }> {
    const { elementString, planType, amount } = args;

    await this._setup();
    this.selectedPaymentType = undefined;

    if (!this.client) {
      return {};
    }

    const options: StripeElementsOptionsMode = {
      mode: 'setup',
      amount,
      currency: 'usd',
      paymentMethodCreation: 'manual',
      appearance: {
        variables: {
          colorPrimary: this.#getPlanColour(planType),
          // Note: fontFamily is same as $FONT_FAMILY_SANS_SERIF
          borderRadius: '3px',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
          fontSizeBase: '13px',
          fontSmooth: 'always',
          spacingGridRow: '6.5px'
        },
        labels: 'floating',
        rules: {
          '.AccordionItem': {
            backgroundColor: '#F3F6F9'
          },
          '.AccordionItem--selected': {
            backgroundColor: '#fff'
          },
          '.Input': {
            padding: '4px 13px',
            boxShadow: 'unset'
          },
          '.Input:focus': {
            boxShadow: 'inset 0 0 0 1px #dce4ec, 0 0 0 1px #fff, 0 0 0 3px rgba(50, 59, 67, 0.3)',
            borderColor: 'transparent',
            outline: '0'
          },
          '.Input--empty::placeholder': {
            color: '#BBCBDA'
          },
          '.Input--invalid': {
            boxShadow: 'none',
            marginBottom: '9.75px'
          },
          '.Label--resting': {
            color: '#BBCBDA',
            fontWeight: '500',
            opacity: '1'
          },
          '.Error': {
            marginBottom: '3.25px'
          }
        }
      }
    };

    const paymentElementOptions: StripePaymentElementOptions = {
      wallets: {
        applePay: this.applePayConfig,
        googlePay: this.googlePayConfig
      },
      fields: {
        billingDetails: {
          address: {
            country: 'auto',
            postalCode: 'auto'
          }
        }
      },
      // Note: needed in order to make sure google pay doesn't just say 'Pay Later'
      business: { name: 'Later Social' },
      //Note: removes the text By providing your card information, you allow Later Social to charge your card for future payments in accordance with their terms.
      terms: { card: 'never', googlePay: 'never', applePay: 'never' }
    };

    if (this.canGooglePay || this.canApplePay) {
      paymentElementOptions.layout = this.paymentElementLayoutConfig;
    }

    const elements = this.client.elements(options);

    // Create and mount the Payment Element
    const paymentElement = elements.create('payment', paymentElementOptions);
    paymentElement.mount(elementString);

    paymentElement.on('change', (e) => {
      this.selectedPaymentType = e.value.type;
    });

    return { elements, paymentElement };
  }

  /**
   * Create Confirmation Token
   *
   * Use to get information about the payment method before final confirmation.
   */
  async createConfirmationToken(elements: StripeElements): Promise<ConfirmationToken> {
    if (!this.client) {
      assert('client must be set');
    }

    const { error: submitError } = await elements.submit();
    if (submitError) {
      if (this.isWalletSelected && submitError.code === 'incomplete') {
        throw new Error(this.incompleteWalletError);
      }
      throw new Error(submitError?.message);
    }

    const { error, confirmationToken } = await this.client.createConfirmationToken({ elements });
    if (error) {
      this.errors.log('error creating payment method' + JSON.stringify(error));
      throw new Error(error?.message);
    }

    return confirmationToken;
  }

  /**
   * Create Setup Intent
   */
  async #createSetupIntent(confirmationToken: string): Promise<SetupIntent> {
    const response: { setup_intent: SetupIntent } = await fetch('/api/v2/create_setup_intent', {
      method: 'POST',
      body: {
        confirmation_token: confirmationToken
      }
    });
    return response.setup_intent;
  }

  /**
   *
   * Creates a payment method from stripe and runs 3ds when required
   *
   * Returns a payment method id that can be used to create a new subscription or update a customer
   */
  async confirmPaymentMethod(elements: StripeElements, location: 'checkout' | 'settings'): Promise<PaymentMethod> {
    if (!this.client) {
      assert('client must be set');
    }

    this.datadog.addAction('confirmPaymentMethod: started', { location });

    //Note: will run elements.submit()
    const confirmationToken = await this.createConfirmationToken(elements);

    const { card, billing_details } = confirmationToken.payment_method_preview;

    if (card?.country && card.country !== billing_details.address?.country) {
      const countries = getCountryList(this.intl);
      const suggestedCountry = countries.findBy('code', card.country)?.name ?? card.country;

      if (card.wallet?.type === 'google_pay') {
        throw new Error(this.intl.t('checkout.payment.country_mismatch_error_google_pay'));
      } else if (card.wallet?.type === 'apple_pay') {
        throw new Error(this.intl.t('checkout.payment.country_mismatch_error_apple_pay'));
      } else {
        this.datadog.addAction('confirmPaymentMethod: country-mismatch', { location });
        throw new Error(this.intl.t('checkout.payment.country_mismatch_error', { suggestedCountry }));
      }
    }

    const setupIntent = await this.#createSetupIntent(confirmationToken.id);
    this.datadog.addAction('confirmPaymentMethod: createSetupIntent', { location, status: setupIntent.status });
    if (!setupIntent.client_secret || !setupIntent.payment_method) {
      this.errors.log('setupIntent failed to setup correctly', { setupIntent: setupIntent as unknown as JsonObject });
      throw new Error(this.intl.t('alerts.account.subscription.billing.update_card.error'));
    }

    //Note: No 3ds required
    if (setupIntent.status != 'requires_action') {
      this.datadog.addAction('confirmPaymentMethod: no-3ds-required', { location });
      return {
        id: setupIntent.payment_method as string,
        ...confirmationToken.payment_method_preview
      } as PaymentMethod;
    }

    //Note: 3ds required:
    this.segment.track('Stripe-3ds-opened', { location });

    //Note: BE creates setup intent with confirm: true, so we need to use handleNextAction instead of confirmSetup
    const { error } = await this.client.handleNextAction({
      clientSecret: setupIntent.client_secret
    });

    if (error) {
      this.errors.log('error confirming payment method' + JSON.stringify(error));
      this.segment.track('Stripe-3ds-actioned', { location, '3DS-status': 'failed attempt' });
      throw new Error(error?.message);
    }

    this.segment.track('Stripe-3ds-actioned', { location, '3DS-status': 'success' });

    return {
      id: setupIntent.payment_method as string,
      ...confirmationToken.payment_method_preview
    } as PaymentMethod;
  }

  async _setup(): Promise<void> {
    await Promise.all([loadScript('https://js.stripe.com/v3/'), loadScript('https://checkout.stripe.com/checkout.js')]);
    if (!this.client && this.stripePublishableKey) {
      this.client = Stripe(this.stripePublishableKey);
    }
    return;
  }

  #getPlanColour(planType?: PlanType): string {
    switch (planType) {
      case 'creator':
        return '#FFD200';
      case 'starter':
        return '#4DBC77';
      case 'growth':
        return '#02ADFF';
      case 'advanced':
        return '#FF9CF7';
      case 'scale-15':
      case 'scale-30':
        return '#FFAE73';
      default:
        return '';
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    stripe: StripeService;
  }
}
